import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppHeader } from "@/components/layout/AppChrome";
import { useI18n } from "@/contexts/I18nContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check } from "lucide-react";

const INDUSTRIES = [
  { en: "E-commerce", ar: "التجارة الإلكترونية" },
  { en: "Restaurant / Cafe", ar: "مطعم أو مقهى" },
  { en: "Clinic / Wellness", ar: "عيادة أو صحة" },
  { en: "Fitness", ar: "اللياقة البدنية" },
  { en: "SaaS", ar: "منتج برمجي (SaaS)" },
  { en: "Education", ar: "التعليم" },
  { en: "Real estate", ar: "العقارات" },
  { en: "Creator / Influencer", ar: "صانع محتوى أو مؤثّر" },
  { en: "Other", ar: "أخرى" },
];
const CHANNELS = ["Instagram", "TikTok", "X / Twitter", "LinkedIn", "YouTube", "Facebook", "Snapchat", "Threads"];
const GOALS = [
  { en: "Grow followers", ar: "زيادة المتابعين" },
  { en: "Increase engagement", ar: "زيادة التفاعل" },
  { en: "Generate leads", ar: "توليد العملاء المحتملين" },
  { en: "Launch product", ar: "إطلاق منتج" },
  { en: "Brand awareness", ar: "زيادة الوعي بالعلامة التجارية" },
  { en: "Community building", ar: "بناء مجتمع المتابعين" },
];

export default function OnboardingPage() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialPlan = params.get("plan") || "pro";
  const initialCycle = params.get("cycle") || "monthly";

  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    business_name: "",
    industry: INDUSTRIES[0].en,
    description: "",
    target_audience: "",
    goals: [],
    social_channels: [],
    budget: "",
    plan_id: initialPlan,
    billing_cycle: initialCycle,
  });

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data.plans || [])); }, []);

  const toggle = (field, value) => {
    setForm((f) => {
      const arr = f[field].includes(value) ? f[field].filter((v) => v !== value) : [...f[field], value];
      return { ...f, [field]: arr };
    });
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, budget: form.budget ? parseFloat(form.budget) : null };
      const { data } = await api.post("/onboarding", payload);
      // -------- Payment routing --------
      // When the backend runs with the mocked-payment bypass disabled (the
      // production default), it now returns `requires_payment: true` and
      // writes the subscription as `awaiting_payment`. We must NOT drop the
      // user onto the dashboard in that state — they'd see a locked
      // experience with no path forward. Instead, we route into the Plans
      // page which hosts the real Tap Payments card-collection UI. The
      // chosen plan + billing cycle are passed through so the Tap flow can
      // start with the correct amount + agreement.
      if (data?.requires_payment) {
        toast.success(t.onboarding.complete || "Profile saved");
        const q = new URLSearchParams({
          plan_id: form.plan_id,
          billing_cycle: form.billing_cycle,
          from: "onboarding",
        });
        nav(`/app/plans?${q.toString()}`);
        return;
      }
      // Legacy dev-only path (mock payments enabled): straight to dashboard.
      toast.success(t.onboarding.complete || "Onboarding complete!");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 py-12">
        <div data-testid="onboarding-steps" className="flex items-center gap-2 mb-8">
          {[1, 2, 3].map((n) => (
            <React.Fragment key={n}>
              <div data-testid={`onboarding-step-${n}`}
                className={`w-8 h-8 rounded-sm flex items-center justify-center text-sm font-medium ${step >= n ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                {step > n ? <Check className="w-4 h-4" /> : n}
              </div>
              {n < 3 && <div className={`h-px flex-1 ${step > n ? "bg-primary" : "bg-border"}`} />}
            </React.Fragment>
          ))}
        </div>

        <h1 className="font-display text-3xl md:text-4xl mb-2">{t.onboarding.title}</h1>
        <p className="text-muted-foreground mb-8">
          {step === 1 ? t.onboarding.step1 : step === 2 ? t.onboarding.step2 : t.onboarding.step3}
        </p>

        {step === 1 && (
          <div data-testid="onboarding-step1" className="space-y-4 nx-card p-6">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.onboarding.businessName}</span>
              <input data-testid="ob-business-name" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                className="mt-2 w-full bg-background border border-border rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary transition outline-none" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.onboarding.industry}</span>
              <select data-testid="ob-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="mt-2 w-full bg-background border border-border rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none">
                {INDUSTRIES.map((i) => <option key={i.en} value={i.en}>{lang === "ar" ? i.ar : i.en}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.onboarding.description}</span>
              <textarea data-testid="ob-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
                className="mt-2 w-full bg-background border border-border rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.onboarding.targetAudience}</span>
              <input data-testid="ob-audience" value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                className="mt-2 w-full bg-background border border-border rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
            </label>
            <div className="pt-2 flex justify-end">
              <button data-testid="ob-next-1" onClick={() => setStep(2)} disabled={!form.business_name} className="nx-btn-primary">{t.common.next}</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboarding-step2" className="space-y-6 nx-card p-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t.onboarding.goals}</div>
              <div className="flex flex-wrap gap-2">
                {GOALS.map((g) => (
                  <button key={g.en} type="button" data-testid={`ob-goal-${g.en}`} onClick={() => toggle("goals", g.en)}
                    className={`px-3 py-2 text-sm rounded-sm border transition ${form.goals.includes(g.en) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                    {lang === "ar" ? g.ar : g.en}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{t.onboarding.channels}</div>
              <div className="flex flex-wrap gap-2">
                {CHANNELS.map((c) => (
                  <button key={c} type="button" data-testid={`ob-channel-${c}`} onClick={() => toggle("social_channels", c)}
                    className={`px-3 py-2 text-sm rounded-sm border transition ${form.social_channels.includes(c) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{t.onboarding.budget}</span>
              <input data-testid="ob-budget" type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                className="mt-2 w-full bg-background border border-border rounded-sm px-4 py-3 focus:border-primary focus:ring-1 focus:ring-primary outline-none" />
            </label>
            <div className="pt-2 flex justify-between">
              <button data-testid="ob-back-2" onClick={() => setStep(1)} className="nx-btn-secondary">{t.common.back}</button>
              <button data-testid="ob-next-2" onClick={() => setStep(3)} className="nx-btn-primary">{t.common.next}</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="onboarding-step3" className="space-y-4">
            <div className="inline-flex items-center bg-background border border-border rounded-sm p-1 mb-2">
              <button data-testid="ob-cycle-monthly" onClick={() => setForm({ ...form, billing_cycle: "monthly" })}
                className={`px-4 py-2 text-sm rounded-sm ${form.billing_cycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {lang === "ar" ? "شهري" : "Monthly"}
              </button>
              <button data-testid="ob-cycle-year" onClick={() => setForm({ ...form, billing_cycle: "yearly" })}
                className={`px-4 py-2 text-sm rounded-sm ${form.billing_cycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {lang === "ar" ? "سنوي" : "Yearly"}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map((p) => (
                <div key={p.id} data-testid={`ob-plan-${p.id}`} onClick={() => setForm({ ...form, plan_id: p.id })}
                  className={`nx-card p-6 cursor-pointer transition ${form.plan_id === p.id ? "border-primary ring-1 ring-primary" : "hover:border-primary/60"}`}>
                  <div className="font-display text-xl">{lang === "ar" ? (p.name_ar || p.name) : p.name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="font-display text-3xl">{p.prices?.[form.billing_cycle] ?? "—"}</span>
                    <span className="text-xs text-muted-foreground">{p.currency || "SAR"} / {form.billing_cycle === "monthly" ? (lang === "ar" ? "شهر" : "mo") : (lang === "ar" ? "سنة" : "yr")}</span>
                  </div>
                  <ul className="mt-4 space-y-2 text-xs">
                    {p.features.slice(0, 4).map((f, i) => <li key={i} className="flex gap-1"><Check className="w-3 h-3 text-primary shrink-0 mt-0.5" /><span>{f}</span></li>)}
                  </ul>
                </div>
              ))}
            </div>
            <div className="pt-2 flex justify-between">
              <button data-testid="ob-back-3" onClick={() => setStep(2)} className="nx-btn-secondary">{t.common.back}</button>
              <button data-testid="ob-submit-btn" onClick={submit} disabled={busy} className="nx-btn-accent">{busy ? t.common.loading : t.onboarding.complete}</button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Payments are MOCKED in Sprint 1. Your subscription will be activated immediately.</p>
          </div>
        )}
      </main>
    </div>
  );
}
